import { useEffect, type RefObject } from 'react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Keeps Tab inside a modal container and restores focus to whatever was focused
 * before it opened. Shared by the teleprompter and camera overlays.
 */
export function useFocusTrap<T extends HTMLElement>(ref: RefObject<T | null>, active = true) {
  useEffect(() => {
    const node = ref.current
    if (!active || !node) return

    const previouslyFocused = document.activeElement as HTMLElement | null

    const focusable = () =>
      Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
      )

    // Move focus into the dialog so the trap and its key handlers apply.
    if (!node.contains(document.activeElement)) {
      ;(focusable()[0] ?? node).focus()
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const items = focusable()
      if (items.length === 0) {
        e.preventDefault()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const active_ = document.activeElement

      if (e.shiftKey && (active_ === first || !node.contains(active_))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active_ === last) {
        e.preventDefault()
        first.focus()
      }
    }

    node.addEventListener('keydown', onKeyDown)
    return () => {
      node.removeEventListener('keydown', onKeyDown)
      previouslyFocused?.focus?.()
    }
  }, [ref, active])
}
